import { useState, useRef, useCallback } from "react";
import ArrowRight from "@mui/icons-material/ArrowRight";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import useOutsideClick from "../useOutsideClick";
import LoadingSpinner from "../../LoadingSpinner";
import CycleExecutionsTable from "./CycleExecutionsTable";
import UIRTMButton from "../UIRTMButton";
import AIAnalytics from "../aiAnalytics/AIAnalytics";

export default function ExecutionsCycleList({
  cycles,
  onFetchExecutions,
  onExportCycle,
  onUploadCycle,
}) {
  const [openCycleId, setOpenCycleId] = useState(null);
  const [showExecutionUploadMenu, setShowExecutionUploadMenu] = useState(false);
  const uploadRef = useRef(null);

  useOutsideClick({
    refs: [uploadRef],
    enabled: showExecutionUploadMenu,
    handler: () => setShowExecutionUploadMenu(false),
  });

  const handleToggle = (cycle) => {
    setOpenCycleId(cycle.cycleId === openCycleId ? null : cycle.cycleId);

    if (!cycle.executionsLoaded && !cycle.loading) {
      onFetchExecutions(cycle.cycleId);
    }
  };

  const getAnalyticsData = useCallback(async (cycle) => {
    // If executions already loaded, just return them formatted
    if (cycle.executionsLoaded && cycle.executions.length > 0) {
      return cycle.executions.map((e) => ({
        "Test Case": e.testKey,
        Status: e.status,
        Priority: e.priority || "Medium",
        "Executed By": e.executedBy,
        Defects: e.defects?.map((d) => d.key).join(",") || "",
      }));
    }
    // Otherwise we might need to fetch them (simplified for now since they load on accordion open)
    return [];
  }, []);

  return (
    <div className="zephyr-cycles">
      {cycles.map((cycle) => {
        const isOpen = openCycleId === cycle.cycleId;

        return (
          <div key={cycle.cycleId} className="cycle-accordion">
            <div
              className={`cycle-header ${isOpen ? "open" : ""}`}
              onClick={() => handleToggle(cycle)}
            >
              <div className="cycle-header-left">
                {isOpen ? <ArrowDropDown /> : <ArrowRight />}
                <span className="cycle-name">{cycle.cycleName}</span>
              </div>

              <div className="cycle-meta">
                {cycle.executions.length > 0 ? (
                  <span>
                    <strong>{cycle.executions.length}</strong> executions
                  </span>
                ) : null}

                <span className="env-pill">
                  Env : {cycle.environment || "-"}
                </span>

                <span className="build-pill">Build : {cycle.build || "-"}</span>
              </div>
            </div>

            {isOpen && (
              <div className="cycle-body">
                {cycle.executions.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      marginBottom: 10,
                    }}
                  >
                    <UIRTMButton
                      title={
                        !cycle.exportLoading ? "Download" : "...Generating"
                      }
                      variant="text"
                      icon={
                        <FileDownloadOutlinedIcon sx={{ fontSize: "1rem" }} />
                      }
                      loading={cycle.exportLoading}
                      disabled={cycle.exportLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExportCycle(cycle);
                      }}
                    />

                    <div className="upload-wrapper" ref={uploadRef}>
                      <UIRTMButton
                        title={!cycle.uploadLoading ? "Upload" : "...Uploading"}
                        icon={<UploadFileIcon sx={{ fontSize: "0.98rem" }} />}
                        endIcon={<ArrowDropDown />}
                        variant="text"
                        loading={cycle.uploadLoading}
                        disabled={cycle.uploadLoading}
                        onClick={() =>
                          setShowExecutionUploadMenu((prev) => !prev)
                        }
                      />

                      {showExecutionUploadMenu && (
                        <div className="upload-menu">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              onUploadCycle(cycle, "sharedDrive");
                            }}
                          >
                            Network Shared Drive
                          </div>
                        </div>
                      )}
                    </div>

                    <AIAnalytics
                      templateType="executionRtm"
                      onFetchData={async () => {
                        // Format the already loaded executions for the dashboard
                        return cycle.executions.map((e) => ({
                          "Cycle ID": cycle.cycleId,
                          "Cycle Name": cycle.cycleName,
                          ExecutionId: e.executionId,
                          "Issue Key": e.testKey,
                          Summary: e.testSummary,
                          Status: e.status,
                          Priority: e.priority || "Unknown",
                          "Executed By": e.executedBy,
                          "Executed On": e.executedOn,
                          Defects: e.defects?.map((d) => d.key).join(",") || "",
                          Version: cycle.version || "",
                          Environment: cycle.environment || "",
                          Build: cycle.build || "",
                        }));
                      }}
                      disabled={
                        !cycle.executionsLoaded || cycle.executions.length === 0
                      }
                    />
                  </div>
                ) : null}

                {cycle.loading ? (
                  <LoadingSpinner small message="" />
                ) : (
                  <>
                    <CycleExecutionsTable executions={cycle.executions} />

                    {/*  {cycle.pagination.hasMore && (
                      <button
                        className="generate-report-btn"
                        onClick={() => onFetchExecutions(cycle.cycleId)}
                      >
                        Load more
                      </button>
                    )} */}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
